import * as React from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import ApplicantsTable from './ApplicantsTable';
import ApplicantsInvitationsTable from './ApplicantsInvitationsTable';
import HelpIconWithTooltip from './HelpIconWithTooltip';
import { useAllApplicants } from "./hooks/useAllApplicants";
import { ApplicantsContext } from './ManageApplicants';
import { fetchProfileNotes } from '../../notes/utils/fetchProfileNotes';
import { Pagination } from '@/shared/components/navigation/Pagination';

// React.useContext - exactly matching ManageApplicants
export const AllApplicantsContext = React.createContext<[any, any, any]>([null, null, null])

const TAB_KEYS = ['applicants', 'less_qualified', 'invited', 'hidden'] as const;

const AllApplicantsManage = (props) => {
  const {
    initial_data,
    authenticity_token,
  } = props;

  // Use the hook to get all applicants data
  const { applicants, loading, error, statistics, filterOptions, pagination, filters, navigateTo, switchTab, updateFilters, patchApplication, refetch } = useAllApplicants();

  // Extract favorites from initial_data
  const favorites = initial_data?.favorites || [];

  const [notes, setNotes] = React.useState([]);
  const [notesLoaded, setNotesLoaded] = React.useState(true);

  const hashToIndex = () => {
    switch (window.location.hash.substr(1)) {
      case 'invited': return 2;
      case 'less_qualified': return 1;
      case 'deleted': return 3;
      default: return 0;
    }
  };

  const [selectedTab, setSelectedTab] = React.useState(hashToIndex());

  // Sync initial tab to hook on mount
  React.useEffect(() => {
    switchTab(TAB_KEYS[hashToIndex()] || 'applicants');
  }, []);

  const getNotes = () => fetchProfileNotes({
    callback: data => { setNotes(data); setNotesLoaded(true); }
  });

  React.useEffect(() => getNotes(), []);

  const handleTabClick = (tabIndex: number) => {
    setSelectedTab(tabIndex);
    switchTab(TAB_KEYS[tabIndex] || 'applicants');
  };

  // Optimistic patch instead of a delayed refetch: ApplicationStatus calls this
  // BEFORE its API request fires, so a refetch scheduled here races that write —
  // when the read wins, stale status flows down and reverts the control. On API
  // failure ApplicationStatus calls back with the old status, which re-patches.
  const updateStatus = (application, status) => {
    if (!application || !application.id) return;
    patchApplication(application.id, { status });
  };

  const hideProfile = (applicant) => {
    if (!confirm('Are you sure you want to hide this applicant?')) return;

    fetch(`/job_applications/${applicant.id}/hide.json`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': authenticity_token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ hidden: !applicant.hidden })
    }).then(() => {
      setTimeout(() => refetch(), 500);
    });
  };

  const unhideProfile = (applicant) => {
    if (!confirm('Are you sure you want to restore this applicant?')) return;

    fetch(`/job_applications/${applicant.id}/unhide.json`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': authenticity_token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ hidden: false })
    }).then(() => {
      setTimeout(() => refetch(), 500);
    });
  };

  React.useEffect(() => {
    switch (selectedTab) {
      case 0: window.location.hash = '#applicants'; break;
      case 1: window.location.hash = '#less_qualified'; break;
      case 2: window.location.hash = '#invited'; break;
      case 3: window.location.hash = '#deleted'; break;
      default: window.location.hash = '#applicants';
    }
  }, [selectedTab]);

  const regularCount = statistics.regular_count ?? 0;
  const lessQualifiedCount = statistics.less_qualified_count ?? 0;
  const invitedCount = statistics.invited_count ?? 0;
  const hiddenCount = statistics.hidden_count ?? 0;

  const hasActiveFilters = filters.nameFilter || filters.dateFilter || filters.statusFilter ||
    filters.locationFilter || filters.positionTypeFilter || filters.jobTypeFilter;

  const renderPagination = () => {
    if (!pagination || pagination.total_pages <= 1) return null;
    return (
      <Pagination
        current_page={pagination.current_page}
        total_pages={pagination.total_pages}
        records={pagination.total_count}
        prev_page={Math.max(1, pagination.current_page - 1)}
        next_page={Math.min(pagination.total_pages, pagination.current_page + 1)}
        navigateTo={navigateTo}
      />
    );
  };

  return (
    <ApplicantsContext.Provider value={{ hideProfile, unhideProfile, updateStatus }}>
      <div className="management-workspace management-workspace--applicants">
        <header className="management-workspace__header">
          <div>
            <div className="management-workspace__eyebrow">Hiring workspace</div>
            <h1 className="management-workspace__title">Applicants</h1>
            <p className="management-workspace__subtitle">
              Review every candidate, update application progress, and keep follow-ups moving across all of your jobs.
            </p>
          </div>
          <div className="management-workspace__actions">
            <a className="management-workspace__action" href="/jobs/manage">
              <i className="fa fa-briefcase" aria-hidden="true"></i>
              Manage jobs
            </a>
            <a className="management-workspace__action management-workspace__action--primary" href="/jobs/new">
              <i className="fa fa-plus" aria-hidden="true"></i>
              Post a job
            </a>
          </div>
        </header>

        <Tabs
          className="management-tabs"
          selectedIndex={selectedTab}
          onSelect={handleTabClick}
        >
        <TabList className="management-tabs__nav" style={{ '--management-tab-count': 4 } as React.CSSProperties}>
          <Tab className="management-tabs__tab">
            <span className="management-tabs__icon"><i className="fa fa-users" aria-hidden="true"></i></span>
            <span className="management-tabs__copy">
              <span className="management-tabs__label">
                Applicants
                {statistics.unseen_count > 0 && <span className="management-tabs__new">New</span>}
              </span>
              <span className="management-tabs__meta">Ready to review</span>
            </span>
            <span className="management-tabs__count">{regularCount}</span>
          </Tab>
          <Tab className="management-tabs__tab">
            <span className="management-tabs__icon"><i className="fa fa-seedling" aria-hidden="true"></i></span>
            <span className="management-tabs__copy">
              <span className="management-tabs__label">
                Dental Newbies <HelpIconWithTooltip message="newbie" style={{ display: 'inline', lineHeight: '4px', margin: 0, padding: 0, fontSize: 12 }} />
              </span>
              <span className="management-tabs__meta">Developing experience</span>
            </span>
            <span className="management-tabs__count">{lessQualifiedCount}</span>
          </Tab>
          <Tab className="management-tabs__tab">
            <span className="management-tabs__icon"><i className="fa fa-paper-plane" aria-hidden="true"></i></span>
            <span className="management-tabs__copy">
              <span className="management-tabs__label">Invited</span>
              <span className="management-tabs__meta">Invitations sent</span>
            </span>
            <span className="management-tabs__count">{invitedCount}</span>
          </Tab>
          <Tab className="management-tabs__tab">
            <span className="management-tabs__icon"><i className="fa fa-archive" aria-hidden="true"></i></span>
            <span className="management-tabs__copy">
              <span className="management-tabs__label">Archived</span>
              <span className="management-tabs__meta">Hidden applications</span>
            </span>
            <span className="management-tabs__count">{hiddenCount}</span>
          </Tab>
        </TabList>

        {error && (
          <div className="alert alert-danger" role="alert">
            Error loading applicants: {error}
          </div>
        )}

        <div className="management-panel">
          <TabPanel className="management-panel">
            <p className="management-panel__intro">
              <i className="fa fa-info-circle" aria-hidden="true"></i>
              Candidates who applied to any of your open or previous jobs.
            </p>

            <ApplicantsTable
              favorites={favorites}
              applicants={applicants}
              authenticity_token={authenticity_token}
              notes={notes}
              load_notes={getNotes}
              loaded={notesLoaded}
              timezone={initial_data?.timezone || 'America/New_York'}
              externalFilters={filters}
              onExternalFiltersChange={updateFilters}
              filterOptions={filterOptions}
              isLoading={loading}
            />

            {!loading && applicants.length === 0 && (
              <div className="management-empty-state">
                <i className="fa fa-search" aria-hidden="true"></i>
                <h4>{hasActiveFilters ? 'No candidates match your filters.' : 'No applicants yet.'}</h4>
                <p>{hasActiveFilters ? 'Try clearing one or more filters.' : 'New applicants will appear here as soon as they apply.'}</p>
              </div>
            )}

            {renderPagination()}

            {lessQualifiedCount > 0 && (
              <div className="text-center">
                <button type="button" className="management-workspace__action mt-4" onClick={() => handleTabClick(1)}>
                  View {lessQualifiedCount} Dental Newbies
                </button>
              </div>
            )}

          </TabPanel>
          <TabPanel className="management-panel">
            <p className="management-panel__intro">
              <i className="fa fa-info-circle" aria-hidden="true"></i>
              Candidates who answered &quot;no&quot; to one or more experience-related screening questions.
            </p>

            <ApplicantsTable
              favorites={favorites}
              applicants={applicants}
              authenticity_token={authenticity_token}
              notes={notes}
              load_notes={getNotes}
              loaded={notesLoaded}
              timezone={initial_data?.timezone || 'America/New_York'}
              externalFilters={filters}
              onExternalFiltersChange={updateFilters}
              filterOptions={filterOptions}
              isLoading={loading}
            />

            {!loading && applicants.length === 0 && (
              <div className="management-empty-state">
                <i className="fa fa-search" aria-hidden="true"></i>
                <h4>{hasActiveFilters ? 'No candidates match your filters.' : 'No Dental Newbies right now.'}</h4>
              </div>
            )}

            {renderPagination()}

          </TabPanel>
          <TabPanel className="management-panel">
            <p className="management-panel__intro">
              <i className="fa fa-info-circle" aria-hidden="true"></i>
              Professionals you invited to apply, grouped across all jobs.
            </p>

            <ApplicantsTable
              favorites={favorites}
              applicants={applicants}
              authenticity_token={authenticity_token}
              notes={notes}
              load_notes={getNotes}
              loaded={notesLoaded}
              timezone={initial_data?.timezone || 'America/New_York'}
              externalFilters={filters}
              onExternalFiltersChange={updateFilters}
              filterOptions={filterOptions}
              isLoading={loading}
            />

            {!loading && applicants.length === 0 && !hasActiveFilters && (
              <div className="management-empty-state">
                <i className="fa fa-paper-plane" aria-hidden="true"></i>
                <h4>You have not invited any candidates to apply.</h4>
                <p>
                  Find candidates faster with Matched Candidates and Invite to Apply.
                </p>
                <a className="management-workspace__action management-workspace__action--primary" href="/profiles">Search candidates</a>
              </div>
            )}

            {!loading && applicants.length === 0 && hasActiveFilters && (
              <div className="management-empty-state">
                <i className="fa fa-search" aria-hidden="true"></i>
                <h4>No candidates match your filters.</h4>
              </div>
            )}

            {applicants.length > 0 && (
              <ApplicantsInvitationsTable
                favorites={favorites}
                applicants={applicants}
                authenticity_token={authenticity_token}
                notes={notes}
                load_notes={getNotes}
                loaded={notesLoaded}
                timezone={initial_data?.timezone || 'America/New_York'}
              />
            )}

            {renderPagination()}
          </TabPanel>
          <TabPanel className="management-panel">
            <p className="management-panel__intro">
              <i className="fa fa-info-circle" aria-hidden="true"></i>
              Hidden applicants stay here so you can restore them later.
            </p>

            <ApplicantsTable
              favorites={favorites}
              applicants={applicants}
              authenticity_token={authenticity_token}
              notes={notes}
              load_notes={getNotes}
              loaded={notesLoaded}
              timezone={initial_data?.timezone || 'America/New_York'}
              isHiddenList={true}
              externalFilters={filters}
              onExternalFiltersChange={updateFilters}
              filterOptions={filterOptions}
              isLoading={loading}
            />

            {!loading && applicants.length === 0 && (
              <div className="management-empty-state">
                <i className="fa fa-archive" aria-hidden="true"></i>
                <h4>{hasActiveFilters ? 'No candidates match your filters.' : 'No hidden applicants.'}</h4>
              </div>
            )}

            {renderPagination()}
          </TabPanel>
        </div>
        </Tabs>
      </div>
    </ApplicantsContext.Provider>
  )
}

export default AllApplicantsManage;
